import { clsx } from "clsx";
import { useRadioProps } from "./useRadioProps";
import classes from "./Radio.module.scss";
import { RadioCardProps } from "./RadioCardProps";
import { useMergeRefs } from "@/hooks/useMergeRefs";
import { forwardRef, useEffect, useId, useRef } from "react";
import { CheckCircle } from "@/components/ui/Icon/CheckCircle";
import { StarCircle } from "@/components/ui/Icon/StarCircle";

const interactiveElements = ["button", "input", "textarea", "select"];

export const RadioCard = forwardRef<HTMLInputElement, RadioCardProps>(
  function RadioCard(
    props: RadioCardProps,
    inputRef: React.Ref<HTMLInputElement>,
  ) {
    const titleId = useId();
    const innerRef = useRef<HTMLInputElement>(null);

    const refs = useMergeRefs(inputRef, innerRef);

    const contentRef = useRef<HTMLDivElement>(null);
    const elementToFocus = useRef<HTMLElement | null | undefined>(null);

    const {
      className,
      ariaLabel,
      title,
      description,
      children,
      variant = "primary",
      image,
      recommended,
      recommendedText,
      disableSelectedStyle,
      ...inputProps
    } = useRadioProps<RadioCardProps>(props);

    useEffect(() => {
      elementToFocus.current = contentRef.current?.querySelector(
        interactiveElements.join(", "),
      );
    }, []);

    const handleClick = (ev: React.MouseEvent) => {
      ev.stopPropagation();

      if (elementToFocus.current) {
        elementToFocus.current.focus();
      } else {
        innerRef.current?.focus();
      }
    };

    const handleFocus = () => innerRef.current?.click();

    const hasImage = Boolean(image);

    return (
      <div
        role="group"
        aria-label={ariaLabel}
        aria-labelledby={title ? titleId : undefined}
        className={clsx(classes.radioCard, className, {
          [classes.primary]: variant === "primary",
          [classes.secondary]: variant === "secondary",
          [classes.disableSelectedStyle]: disableSelectedStyle,
        })}
        onClick={handleClick}
        onFocus={handleFocus}
      >
        <input
          ref={refs}
          type="radio"
          {...inputProps}
          className={classes.radioCardInput}
        />

        <div
          ref={contentRef}
          className={clsx(
            classes.radioCardLayout,
            hasImage && classes.radioCardLayoutWithImage,
          )}
        >
          <div className={classes.radioCardTextContent}>
            {title && (
              <div id={titleId} className={classes.radioCardTitle}>
                {title}{" "}
                {recommended && (
                  <span className={classes.recommended}>
                    <StarCircle
                      size={20}
                      color="#E5AA17"
                      className={classes.recommendedIcon}
                    />
                    {recommendedText && (
                      <span className={classes.recommendedText}>
                        {recommendedText}
                      </span>
                    )}
                  </span>
                )}
              </div>
            )}

            {description && (
              <div className={classes.radioCardDescription}>{description}</div>
            )}
          </div>

          {image && (
            <div className={classes.radioCardImage}>
              <img src={image} alt="" />
            </div>
          )}

          {children && (
            <div className={classes.radioCardContent}>{children}</div>
          )}
        </div>

        <CheckCircle
          size={24}
          color="currentColor"
          className={classes.checkIcon}
        />
      </div>
    );
  },
);
